import type { SkyPresetName } from '../prompt-showcase/golden-hour-sky'
import type { AppVisualChrome } from './app-visual-stage'

/**
 * The marketing pages' app visuals, one per section that shows the product:
 * the home page's five, then the audience pages' (`/marketers`, `/startups`,
 * `/agencies`, `/ecommerce`).
 */
export type AppVisualKind =
  | 'brandGuide'
  | 'canvas'
  | 'send'
  | 'automation'
  | 'mcp'
  | 'metrics'
  | 'liveMetrics'
  | 'contacts'
  | 'collaboration'
  | 'growthTools'
  | 'developer'
  | 'brands'
  | 'remix'
  | 'figma'
  | 'shopify'

export type AppVisualLayout = {
  /** The stage's true-scale size; the page scales it to the column. */
  width: number
  height: number
  chrome: AppVisualChrome
  innerClassName?: string
  /** The sky behind the scene; neighbouring visuals never share one. */
  sky: SkyPresetName
  /**
   * A working copy of the product the visitor can click and edit, or (when
   * false) a picture of it, `inert` and labelled as one image.
   */
  isInteractive: boolean
  /** What the visual shows: the frame's accessible name. */
  label: string
}

/**
 * Stage size, chrome and description per visual. Kept apart from the
 * scenes so the page and the lab can lay a stage out without loading any
 * scene's code.
 */
export const APP_VISUAL_LAYOUT: Record<AppVisualKind, AppVisualLayout> = {
  brandGuide: {
    width: 1024,
    height: 680,
    chrome: 'bare',
    sky: 'marigold',
    isInteractive: true,
    label:
      "Brew's brand model for six brands (Brew, LiveFlow, Cursor, Clay, Claude and Ramp): pick a brand to see its email-design.md colors, its logos and images, the brand-ready card, image-style.md, and its first email, ready to send.",
  },
  canvas: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'apricot',
    isInteractive: true,
    label:
      'The Brew canvas editing a welcome email: click an element to select it and style it in the inspector beside it, click again to change its text.',
  },
  send: {
    width: 1024,
    height: 560,
    chrome: 'inset',
    sky: 'marigoldLate',
    isInteractive: true,
    label:
      "Brew's send dialog for Nume's headcount planning launch, scheduled for tomorrow at 9:00 AM: edit the envelope, pick an audience, choose when to send, and run the audit.",
  },
  automation: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'apricot',
    isInteractive: true,
    label:
      "Brew's automation triggers: the connected Clerk and Stripe events, with Shopify, WorkOS and more sources one click away, and the automation a Clerk sign-up starts, sending the Welcome to Brew email. Drag the canvas to pan and move the cards.",
  },
  mcp: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigold',
    isInteractive: true,
    label:
      "Brew's MCP setup: pick an agent (Claude, ChatGPT, Cursor and more) to see its connection steps, and copy the brew.new MCP URL.",
  },
  metrics: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'apricot',
    isInteractive: false,
    label:
      "A campaign's results in Brew a week after it went out: delivered, opened and clicked with their rates, a chart of opens and clicks by day, and the email itself.",
  },
  liveMetrics: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigoldLate',
    isInteractive: false,
    label:
      'A Beats campaign two hours into its send in Brew: deliveries, opens and clicks so far, a live chart of opens and clicks every ten minutes, and the email itself.',
  },
  contacts: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigold',
    isInteractive: false,
    label:
      "Brew's contacts: all 1,284,902 contacts filtered by plan, opens and country, the first rows with their validation status, and a CSV import's column mapping.",
  },
  collaboration: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'goldenHour',
    isInteractive: false,
    label:
      "Brew's welcome email on the canvas with teammates in it: their live cursors, a comment on the headline and its reply thread.",
  },
  growthTools: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigold',
    isInteractive: false,
    label:
      "Brew's growth tools: a finished contact validation run with each address's verdict, the sending domain's health, and the welcome email as Gmail renders it.",
  },
  developer: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'apricot',
    isInteractive: false,
    label:
      'One email send three ways: the TypeScript SDK, the REST API as a cURL request, and installing the CLI and SDK.',
  },
  brands: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigoldLate',
    isInteractive: false,
    label:
      "An agency's client brands in one Brew organization (LiveFlow, Cursor, Clay, Claude and Ramp, each with its emails and people) and the Share menu inviting two client reviewers.",
  },
  remix: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'apricot',
    isInteractive: false,
    label:
      "A Sonos email from the template gallery and the chat turn remixing it into a new on-brand email, with the email agent's finished run.",
  },
  figma: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'goldenHour',
    isInteractive: false,
    label:
      'A Figma frame pasted into the Brew chat, its finished import, and the editable email it became.',
  },
  shopify: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigold',
    isInteractive: false,
    label:
      "Brew's Shopify triggers (order created, checkout abandoned, order fulfilled) and the flow an order starts, sending Tracksmith's Built for the Blizzard email.",
  },
}
